import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Pressable, ScrollView, Image } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import {
  ClipboardList,
  User,
  Home,
  Building,
  MapPin,
  Map,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ChevronDown,
  Check,
  Camera,
  Pencil,
} from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { getAdministrativeDivisionLabel } from '../../../../constants/countries';
import useCitiesByCountry from '../../../../hooks/useCitiesByCountry';
import { validateFields, requestOTP, register } from '../../../../services/auth';
import { validateImageFile, compressImageIfNeeded } from '../../../../utils/imageUtils';
import { PLACEHOLDER_PROFILE_BASE64 } from '../../../../constants/placeholderImage';

const TERMS_AND_CONDITIONS_URL =
  'https://docs.google.com/document/d/e/2PACX-1vR7TPQsU-svthrBmcWp0-TFEkVBdcJIBN7ICZoiAI3onDsMbhLprh9exxvMMn3npg/pub?embedded=true';

const StepDetails = ({ formData, updateFormData, goToStep, isDoctor }) => {
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isMobile = useIsMobile();

  const { data: citiesData, isLoading: isLoadingCities } = useCitiesByCountry(formData.country);

  const states = useMemo(() => {
    if (!citiesData || citiesData.length === 0) {
      return [];
    }
    return citiesData.map(state => state.name);
  }, [citiesData]);

  const administrativeDivisionLabel = getAdministrativeDivisionLabel(formData.country);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPassword = (password) => {
    return password && password.length >= 8;
  };

  const validateMutation = useMutation(
    (data) => validateFields(data),
    {
      onError: (err) => {
        // Account exists but not activated — skip to OTP verification
        if (err.response?.status === 302) {
          updateFormData('isActivation', true);
          requestOTPMutation.mutate({ email: formData.email });
          return;
        }
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Validation failed');
        }
      },
      onSuccess: () => {
        // Register user first, then request OTP
        registerMutation.mutate({
          first_name: formData.firstName,
          last_name: '-',
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          address: formData.address,
          city: formData.city,
          postcode: formData.postalCode,
          state: formData.state,
          country: formData.country,
          professions: formData.professions,
          profession_ids: formData.profession_ids,
          profession_id: formData.profession_id,
          expertise: formData.profession_id,
          isSpecialist: false,
          profession_type_code: formData.profession,
          profile_image: formData.profileImage || PLACEHOLDER_PROFILE_BASE64,
          scope: 'user',
          role: 'doctor',
        });
      },
    }
  );

  const registerMutation = useMutation(
    (data) => register(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Registration failed');
        }
      },
      onSuccess: () => {
        requestOTPMutation.mutate({ email: formData.email });
      },
    }
  );

  const requestOTPMutation = useMutation(
    (data) => requestOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Failed to send verification code');
        }
      },
      onSuccess: (res) => {
        updateFormData('nonce', res.data.data.nonce);
        goToStep(4);
      },
    }
  );

  const isValid = formData.firstName && formData.email && isValidEmail(formData.email) &&
    isValidPassword(formData.password) && formData.password === formData.confirmPassword && formData.termsAccepted;
  const isLoading = validateMutation.isLoading || registerMutation.isLoading || requestOTPMutation.isLoading;

  const handleSubmit = () => {
    setError('');
    setFieldErrors({});

    const errors = {};
    if (!formData.firstName) {
      errors.firstName = 'Full name is required';
    }
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!isValidPassword(formData.password)) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.termsAccepted) {
      setError('Please accept the Terms & Conditions');
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    validateMutation.mutate({
      email: formData.email,
      scope: isDoctor ? 'doctor' : 'clinic_owner',
    });
  };

  const selectState = (state) => {
    updateFormData('state', state);
    setShowStateDropdown(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file, 5);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    try {
      const base64 = await compressImageIfNeeded(file);
      updateFormData('profileImage', base64);
      setError('');
    } catch (err) {
      setError('Failed to process image');
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <View style={styles.stepIcon}>
          <ClipboardList size={48} color={webColors.primary} />
        </View>
        <Text style={styles.stepTitle}>Account Credentials</Text>
        <Text style={styles.stepSubtitle}>How can we reach you?</Text>
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.form}>
        {/* Profile Image (Optional) */}
        <View style={styles.formGroup}>
          <View style={styles.imageUploadWrapper}>
            <label style={styles.imageUploadLabel}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={isLoading}
              />
              <View style={styles.profilePictureContainer}>
                <View style={styles.profilePictureWrapper}>
                  {formData.profileImage ? (
                    <Image
                      source={{ uri: 'data:image/jpg;base64,' + formData.profileImage }}
                      style={styles.profileImage}
                    />
                  ) : (
                    <View style={styles.profileImagePlaceholder}>
                      <Camera size={32} color={webColors.textMuted} />
                    </View>
                  )}
                  <View style={styles.profilePictureOverlay}>
                    <Pencil size={14} color="#ffffff" />
                  </View>
                </View>
                <Text style={styles.uploadHintText}>
                  {formData.profileImage ? 'Click to change photo' : 'Click to upload photo'}
                </Text>
              </View>
            </label>
            {formData.profileImage && (
              <TouchableOpacity
                style={styles.removeImageBtn}
                onPress={() => updateFormData('profileImage', null)}
              >
                <Text style={styles.removeImageText}>Remove</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        {/* Full Name */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Full Name *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <User size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, fieldErrors.firstName && styles.inputError]}
              placeholder="Enter your full name"
              placeholderTextColor={webColors.textMuted}
              value={formData.firstName}
              onChangeText={(value) => {
                updateFormData('firstName', value);
                setFieldErrors(prev => ({ ...prev, firstName: null }));
              }}
              editable={!isLoading}
            />
          </View>
          {fieldErrors.firstName && (
            <Text style={styles.fieldError}>{fieldErrors.firstName}</Text>
          )}
        </View>



        {/* Home Address */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Home Address</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Home size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={styles.input}
              placeholder="Street address"
              placeholderTextColor={webColors.textMuted}
              value={formData.address}
              onChangeText={(value) => updateFormData('address', value)}
              editable={!isLoading}
            />
          </View>
        </View>

        {/* City / Town */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>City / Town</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Building size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={styles.input}
              placeholder="Enter city or town"
              placeholderTextColor={webColors.textMuted}
              value={formData.city}
              onChangeText={(value) => updateFormData('city', value)}
              editable={!isLoading}
            />
          </View>
        </View>

        {/* Postal Code & State */}
        <View style={[styles.formRow, { zIndex: 100, position: 'relative' }, isMobile && styles.formRowMobile]}>
          <View style={[styles.formGroup, { flex: 1 }]}>
            <Text style={styles.formLabel}>Postal Code</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.inputIcon}>
                <MapPin size={18} color={webColors.textMuted} />
              </View>
              <TextInput
                style={styles.input}
                placeholder="Postal code"
                placeholderTextColor={webColors.textMuted}
                value={formData.postalCode}
                onChangeText={(value) => updateFormData('postalCode', value)}
                editable={!isLoading}
              />
            </View>
          </View>
          <View style={[styles.formGroup, { flex: 1 }]}>
            <Text style={styles.formLabel}>{administrativeDivisionLabel}</Text>
            <View style={styles.dropdownWrapper}>
              <TouchableOpacity
                style={styles.dropdown}
                onPress={() => setShowStateDropdown(!showStateDropdown)}
                disabled={isLoading}
              >
                <View style={styles.dropdownIcon}>
                  <Map size={18} color={webColors.textMuted} />
                </View>
                <Text style={[
                  styles.dropdownText,
                  !formData.state && styles.dropdownPlaceholder
                ]}>
                  {formData.state || `Select ${administrativeDivisionLabel.toLowerCase()}`}
                </Text>
                <ChevronDown size={16} color={webColors.textMuted} />
              </TouchableOpacity>
              {showStateDropdown && (
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
                    {states.map((state) => (
                      <TouchableOpacity
                        key={state}
                        style={[
                          styles.dropdownItem,
                          formData.state === state && styles.dropdownItemSelected
                        ]}
                        onPress={() => selectState(state)}
                      >
                        <Text style={[
                          styles.dropdownItemText,
                          formData.state === state && styles.dropdownItemTextSelected
                        ]}>
                          {state}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Email Address */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Email Address *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Mail size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, fieldErrors.email && styles.inputError]}
              placeholder="Enter your email"
              placeholderTextColor={webColors.textMuted}
              value={formData.email}
              onChangeText={(value) => {
                updateFormData('email', value);
                setFieldErrors(prev => ({ ...prev, email: null }));
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!isLoading}
            />
          </View>
          {fieldErrors.email && (
            <Text style={styles.fieldError}>{fieldErrors.email}</Text>
          )}
        </View>

        {/* Password */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Password *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Lock size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, styles.inputWithToggle, fieldErrors.password && styles.inputError]}
              placeholder="Create a password (min. 8 characters)"
              placeholderTextColor={webColors.textMuted}
              value={formData.password}
              onChangeText={(value) => {
                updateFormData('password', value);
                setFieldErrors(prev => ({ ...prev, password: null }));
              }}
              secureTextEntry={!showPassword}
              editable={!isLoading}
            />
            <TouchableOpacity
              style={styles.togglePassword}
              onPress={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff size={18} color={webColors.textMuted} />
              ) : (
                <Eye size={18} color={webColors.textMuted} />
              )}
            </TouchableOpacity>
          </View>
          {fieldErrors.password && (
            <Text style={styles.fieldError}>{fieldErrors.password}</Text>
          )}
        </View>

        {/* Confirm Password */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Confirm Password *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Lock size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, styles.inputWithToggle, fieldErrors.confirmPassword && styles.inputError]}
              placeholder="Confirm your password"
              placeholderTextColor={webColors.textMuted}
              value={formData.confirmPassword}
              onChangeText={(value) => {
                updateFormData('confirmPassword', value);
                setFieldErrors(prev => ({ ...prev, confirmPassword: null }));
              }}
              secureTextEntry={!showConfirmPassword}
              editable={!isLoading}
            />
            <TouchableOpacity
              style={styles.togglePassword}
              onPress={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff size={18} color={webColors.textMuted} />
              ) : (
                <Eye size={18} color={webColors.textMuted} />
              )}
            </TouchableOpacity>
          </View>
          {fieldErrors.confirmPassword && (
            <Text style={styles.fieldError}>{fieldErrors.confirmPassword}</Text>
          )}
        </View>

        {/* Terms & Conditions */}
        <Pressable
          style={styles.termsCheck}
          onPress={() => updateFormData('termsAccepted', !formData.termsAccepted)}
          disabled={isLoading}
        >
          <View style={[
            styles.checkbox,
            formData.termsAccepted && styles.checkboxChecked,
          ]}>
            {formData.termsAccepted && <Check size={14} color="#ffffff" />}
          </View>
          <Text style={styles.termsText}>
            I have read and agreed to the{' '}
            <Text
              style={styles.termsLink}
              onPress={(e) => {
                e?.stopPropagation?.();
                if (typeof window !== 'undefined') {
                  window.open(TERMS_AND_CONDITIONS_URL, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              Terms & Conditions
            </Text>
          </Text>
        </Pressable>
      </View>

      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(2)}
          disabled={isLoading}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, (!isValid || isLoading) && styles.btnNextDisabled]}
          onPress={handleSubmit}
          disabled={!isValid || isLoading}
        >
          <Text style={[styles.btnNextText, (!isValid || isLoading) && styles.btnNextTextDisabled]}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  form: {
    marginBottom: 24,
  },
  formGroup: {
    marginBottom: 16,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  formRowMobile: {
    flexDirection: 'column',
    gap: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -9 }],
    zIndex: 1,
  },
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 44,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  inputError: {
    borderColor: webColors.error,
  },
  inputWithToggle: {
    paddingRight: 48,
  },
  togglePassword: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
  },
  eyeIcon: {},
  fieldError: {
    color: webColors.error,
    fontSize: 12,
    marginTop: 4,
  },
  imageUploadWrapper: {
    marginTop: 8,
    alignItems: 'center',
  },
  imageUploadLabel: {
    cursor: 'pointer',
  },
  profilePictureContainer: {
    alignItems: 'center',
  },
  profilePictureWrapper: {
    position: 'relative',
    width: 100,
    height: 100,
  },
  profileImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  profileImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profilePictureOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    backgroundColor: webColors.primary,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  uploadHintText: {
    marginTop: 8,
    fontSize: 14,
    color: webColors.textMuted,
  },
  removeImageBtn: {
    marginTop: 8,
    padding: 8,
  },
  removeImageText: {
    fontSize: 14,
    color: webColors.error,
  },
  dropdownWrapper: {
    position: 'relative',
    zIndex: 10,
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 44,
    paddingRight: 14,
  },
  dropdownIcon: {
    position: 'absolute',
    left: 14,
  },
  dropdownText: {
    flex: 1,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  dropdownPlaceholder: {
    color: webColors.textMuted,
  },
  dropdownArrow: {},
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    marginTop: 4,
    ...webShadows.card,
    zIndex: 999,
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  dropdownItemSelected: {
    backgroundColor: webColors.primaryLight,
  },
  dropdownItemText: {
    fontSize: 15,
    color: webColors.textPrimary,
  },
  dropdownItemTextSelected: {
    color: webColors.primary,
    fontWeight: '600',
  },
  termsCheck: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginTop: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  checkboxMark: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
  },
  termsText: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 22,
    flex: 1,
  },
  termsLink: {
    color: '#3b82f6',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepDetails;
